import { Box, Button, Checkbox, CheckboxGroup, Container, Flex, FormControl, Heading, SimpleGrid, Stack, useToast } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAxios } from 'context/AxiosContextProvider';
import { useUserContext } from 'context/UserContextProvider';
import { endOfDay, format, startOfDay } from 'date-fns';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import useAnnouncementsQuery from 'hooks/useAnnouncementsQuery';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { dateInput, simpleInput, textareaInput } from '../../helpers/makeFormFields';

export interface IAnnouncementForm {
  translation_key: string;
  start: string;
  end: string;
  country: object;
  texts: any;
  inputTexts: { [key: string]: string };
}

const schema = yup
  .object({
    name: yup.string().required("Name can't be empty").nullable(),
    start: yup.date().required('Start date must be selected'),
    end: yup.date().required('End data must be selected'),
    country: yup.array().min(1).of(yup.string().required()).required(),
  })
  .required();

const AnnouncementForm = (props: {
  method: string;
  url: string;
  defaultValues: any;
  locations?: Array<string>;
  record?: {} | undefined;
  isDataLoaded: boolean;
  isUpdate: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const axios = useAxios();
  const queryClient = useQueryClient();
  const {
    config: { languages, countries = {} as Record<number, string> },
  } = useUserContext();
  const keyToRemove = Object.keys(countries).find((key) => countries[parseInt(key)] === 'XI');
  if (keyToRemove !== undefined) {
    delete countries[parseInt(keyToRemove)];
  }

  const { method, url, defaultValues, isUpdate } = props;
  const toast = useToast();

  const formatStartDate = (date: string | null) => {
    if (date === null) return null;
    return format(startOfDay(new Date(date)), 'yyyy-MM-dd HH:mm:ss');
  };

  const formatEndDate = (date: string | null) => {
    if (date === null) return null;
    return format(endOfDay(new Date(date)), 'yyyy-MM-dd HH:mm:ss');
  };

  const {
    handleSubmit,
    formState: { errors, isValid, submitCount },
    control,
    register,
    setError,
    setValue,
    getValues,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const countriesSelected: any = watch('country');
  const texts = watch('texts');

  if (countriesSelected !== false && countriesSelected?.includes('')) {
    setValue('country', Object.values(countries));
  }

  const languagesFromSelectedCountries = countriesSelected
    ? countriesSelected?.reduce((acc: string[], country: string) => {
        languages[country]?.map((language) => {
          if (!acc.includes(language)) {
            acc.push(language);
          }
        });
        return acc;
      }, [])
    : [];

  let startWithAll: { language: string; value: string }[] = texts ?? [];

  const announcementsToDisplay = languagesFromSelectedCountries?.map((language: string) => {
    const existingPromotions = startWithAll?.filter(({ language }) => languagesFromSelectedCountries.includes(language));

    startWithAll = existingPromotions;

    const existingLanguages = startWithAll?.map(({ language }) => language);

    if (!existingLanguages.includes(language)) {
      startWithAll.push({ language, value: '' });
    }

    return [...startWithAll];
  });

  const { mutate, isPending } = useMutation<any, Error, IAnnouncementForm>({
    mutationFn: async (data) => {
      let formData = data;
      // @ts-ignore
      const response = await axios[method](
        url,
        formData,
        // @ts-ignore
      ).catch((err) => {
        return Promise.reject(err);
      });

      return response;
    },
  });

  const onSubmit: SubmitHandler<yup.InferType<typeof schema>> = (data) => {
    // @ts-expect-error
    data.start = formatStartDate(data?.start)!;
    // @ts-expect-error
    data.end = formatEndDate(data?.end)!;
    const textsToSubmit: any[] = [];

    languagesFromSelectedCountries.map((language: string) => {
      // @ts-expect-error
      if (data.texts === undefined) {
        textsToSubmit.push({
          language,
          // @ts-expect-error
          value: data.inputTexts[language],
        });
      } else {
        // @ts-expect-error
        let existingTexts = data.texts.find((text: any) => text.language === language);
        if (existingTexts) {
          textsToSubmit.push({
            ...existingTexts,
            // @ts-expect-error
            value: data.inputTexts[language],
          });
        } else {
          textsToSubmit.push({
            language,
            // @ts-expect-error
            value: data.inputTexts[language],
          });
        }
      }
    });
    const dataForSubmission = {
      ...data,
      texts: textsToSubmit,
      country: countriesSelected.filter((str: string) => str !== ''),
    };

    const { ...rest } = dataForSubmission;

    mutate(rest as any, {
      onSuccess: () => {
        navigate(`/app/op/announcements`);
        // @ts-expect-error
        queryClient.invalidateQueries(useAnnouncementsQuery.getKey());
      },
      onError: () => {
        manageErrorResponse(toast, errors as any);
        (Object.keys(errors) as Array<keyof IAnnouncementForm>).forEach((key) => {
          setError(key, {
            type: 'server',
            message: (errors[key] as any).join('. '),
          });
        });
      },
    });
  };

  const allCountriesSelected = Object.values(countries).every((country) =>
    getValues('country') ? (getValues('country') as any).includes(country) : 'null',
  )
    ? true
    : false;

  const someCountriesSelected =
    Object.values(countries).some((country) => (getValues('country') ? (getValues('country') as any).includes(country) : 'null')) &&
    !Object.values(countries).every((country) => (getValues('country') ? (getValues('country') as any).includes(country) : 'null'))
      ? true
      : false;

  return (
    <Container as="form" onSubmit={handleSubmit(onSubmit)} mx={0}>
      <Stack direction={['column']}>
        {simpleInput({
          name: 'name',
          label: t('forms:fullname.label'),
          register,
          errors,
          schema,
        })}
        <Stack spacing="6" direction={{ base: 'column', md: 'row' }} py={2}>
          {dateInput({
            name: 'start',
            label: t('forms:start.label'),
            control,
            errors,
            schema,
          })}

          {dateInput({
            name: 'end',
            label: t('forms:end.label'),
            control,
            errors,
            schema,
          })}
        </Stack>

        <FormControl {...register('country')}>
          <Heading as="h3" fontSize="xxs">
            {t('forms:country.label')}
          </Heading>
          <CheckboxGroup value={getValues('country') as any}>
            <Checkbox
              isChecked={allCountriesSelected}
              isIndeterminate={someCountriesSelected}
              onChange={() => {
                allCountriesSelected ? setValue('country', []) : setValue('country', Object.values(countries));
              }}
            >
              {t('forms:all_countries.label')}
            </Checkbox>
            <SimpleGrid columns={{ base: 1, md: 4 }} spacing={2} my={4} mb={8}>
              {Object.keys(countries).map((index) => (
                <Checkbox
                  key={index}
                  value={countries[parseInt(index)]}
                  onChange={(e) => {
                    e.target.checked
                      ? setValue('country', [...(getValues('country') as any), countries[parseInt(index)]])
                      : setValue(
                          'country',
                          (getValues('country') as any).filter((country: any) => country != countries[parseInt(index)]),
                        );
                  }}
                >
                  {t(`location:countries.${countries[parseInt(index)]}`)}
                </Checkbox>
              ))}
            </SimpleGrid>
          </CheckboxGroup>
        </FormControl>
      </Stack>

      <Stack direction={['column']}>
        {announcementsToDisplay[announcementsToDisplay.length - 1]?.map((text: any) => {
          return (
            <Box key={text?.language}>
              {textareaInput({
                name: `inputTexts.${text?.language}`,
                label: t(`location:languages.${text?.language}`),
                register,
                errors,
                defaultValue: text?.value,
                required: true,
              })}
            </Box>
          );
        })}
      </Stack>
      <Flex direction="row-reverse" py="4" px={{ base: '4', md: '6' }}>
        <Button
          type="submit"
          variant="primary"
          ml={2}
          disabled={countriesSelected?.length === 0 || countriesSelected === false || (submitCount! > 0 && !isValid) || isPending}
        >
          {isUpdate ? t('common:save') : t('common:create')}
        </Button>
      </Flex>
    </Container>
  );
};

// @ts-ignore
export default AnnouncementForm;
